'use client'

import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { Tools } from '@/@corefabriccanvas';
import { SubToolType, UserContentType } from '@/types';

interface DrawingContextType {
  currentTool: Tools;
  updateSelectedTool: (tool: Tools) => void;
  activeSubTool: SubToolType | undefined;
  updateActiveSubTool: (tool: SubToolType | undefined) => void;
  previousTool: Tools;
  selectedColor: string;
  updateSelectedColor: (color: string, userId: string) => void;
  thickness: number;
  updateThickness: (thickness: number, userId: string) => void;
  eraseThickness: number;
  updateEraseThickness: (eraseThickness: number, userId: string) => void;
  initUserDrawSetting: (userId: string) => void;
  // Note
  isNoteAreaActive: boolean;
  addingNoteType: UserContentType;
  activateNoteArea: (userContentType: UserContentType) => void;
  deactivateNoteArea: () => void;
  //Page Item Position
  pageItemPosition: 'left' | 'right' | 'center';
  updatePageItemPosition: (position: 'left' | 'right' | 'center') => void;
}

const DrawingContext = createContext<DrawingContextType | undefined>(undefined);

interface DrawingContextProviderProps {
  children: ReactNode;
}

export const DrawingContextProvider: React.FC<DrawingContextProviderProps> = ({
  children,
}) => {
  const [isNoteAreaActive, setIsNoteAreaActive] = useState<boolean>(false);
  const [addingNoteType, setAddingNoteType] = useState<UserContentType>(
    UserContentType.Drawing,
  );
  const [selectedColor, setSelectedColor] = useState('#000000');
  const [thickness, setThickness] = useState(5);
  const [eraseThickness, setEraseThickness] = useState(5);

  const [currentTool, setCurrentTool] = useState<Tools>(Tools.Pan);
  const [previousTool, setPreviousTool] = useState<Tools>(Tools.Pan);

  const [activeSubTool, setActiveSubTool] = useState<SubToolType | undefined>(
    undefined,
  );

  const [pageItemPosition, setPageItemPosition] = useState<
    'left' | 'right' | 'center'
  >('left');

  const activateNoteArea = useCallback(
    (userContentType: UserContentType) => {
      setIsNoteAreaActive(true);
      setAddingNoteType(userContentType);
    },
    [isNoteAreaActive],
  );

  const deactivateNoteArea = useCallback(() => {
    setIsNoteAreaActive(false);
  }, []);

  const updateSelectedTool = useCallback(
    (tool: Tools) => {
      setPreviousTool(currentTool);
      setCurrentTool(tool);
    },
    [currentTool],
  );

  const updateActiveSubTool = useCallback(
    (subTool: SubToolType | undefined) => {
      setActiveSubTool(subTool);
    },
    [activeSubTool],
  );
  const updateSelectedColor = useCallback((color: string, userId: string) => {
    setSelectedColor(color);
    localStorage.setItem(`color-${userId}`, color);
  }, []);

  const updateThickness = useCallback((thickness: number, userId: string) => {
    setThickness(thickness);
    localStorage.setItem(`thickness-${userId}`, thickness.toString());
  }, []);

  const updateEraseThickness = useCallback(
    (eraseThickness: number, userId: string) => {
      setEraseThickness(eraseThickness);
      localStorage.setItem(
        `eraserThickness-${userId}`,
        eraseThickness.toString(),
      );
    },
    [],
  );

  // PageItem Position
  const updatePageItemPosition = useCallback(
    (position: 'left' | 'right' | 'center') => {
      setPageItemPosition(position);
    },
    [pageItemPosition],
  );

  const initUserDrawSetting = useCallback((userId: string) => {
    const color = localStorage.getItem(`color-${userId}`);
    const recordedThickness = localStorage.getItem(`thickness-${userId}`);
    const recordedEraseThickness = localStorage.getItem(
      `eraserThickness-${userId}`,
    );

    setSelectedColor(color || '#000000');
    setThickness(Number(recordedThickness) || 5);
    setEraseThickness(Number(recordedEraseThickness) || 5);
  }, []);

  const contextValue = useMemo(
    () => ({
      currentTool,
      updateSelectedTool,
      previousTool,
      initUserDrawSetting,
      isNoteAreaActive,
      addingNoteType,
      activateNoteArea,
      deactivateNoteArea,
      selectedColor,
      updateSelectedColor,
      thickness,
      updateThickness,
      eraseThickness,
      updateEraseThickness,
      activeSubTool,
      updateActiveSubTool,
      pageItemPosition,
      updatePageItemPosition
    }),
    [
      currentTool,
      previousTool,
      isNoteAreaActive,
      addingNoteType,
      selectedColor,
      thickness,
      eraseThickness,
      activateNoteArea,
      deactivateNoteArea,
      updateSelectedTool,
      updateSelectedColor,
      updateThickness,
      updateEraseThickness,
      initUserDrawSetting,
      activeSubTool,
      updateActiveSubTool,
      pageItemPosition,
      updatePageItemPosition
    ],
  );

  return (
    <DrawingContext.Provider value={contextValue}>
      {children}
    </DrawingContext.Provider>
  );
};

export const useDrawingContext = () => {
  const context = useContext(DrawingContext);
  if (!context)
    throw new Error(
      'useDrawingContext must be used within a DrawingContextProvider',
    );
  return context;
};
